import apiClient from "./axios";
import {
  getSavedSessionObj,
  ensureSessionForUser,
} from "../utils/sessionManager";
import { Password } from "@mui/icons-material";

// export const fetchSchema = async (file, userId, sessionId) => {
//   if (!file) {
//     return;
//   }

//   const sid =
//     sessionId ||
//     getSavedSessionObj()?.session_id ||
//     (await ensureSessionForUser(userId));

//   const formData = new FormData();
//   formData.append("file", file);
//   formData.append("user_id", String(userId));
//   formData.append("session_id", String(sid));

//   for (let pair of formData.entries()) {
//   }

//   try {
//     const response = await apiClient.post(
//       "/poirot/fetch_attribute/dtype/v1.0",
//       formData
//     );

//     return response.data;
//   } catch (error) {
//     throw error;
//   }
// };
export const fetchSchema = async (file, userId, sessionId) => {
  if (!file) return;

  const sid =
    sessionId ||
    getSavedSessionObj()?.session_id ||
    (await ensureSessionForUser(userId));

  const formData = new FormData();
  formData.append("file", file);
  formData.append("user_id", String(userId));
  formData.append("session_id", String(sid));

  try {
    const response = await apiClient.post(
      "/poirot/fetch_attribute/dtype/v1.0",
      formData
    );
    return response.data;
  } catch (error) {
    // 🛑 Convert server or network error into readable text
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Unknown error occurred while uploading.";
    // ✅ Re-throw a clean, human-readable error
    throw new Error(message);
  }
};

/**
 * Update existing file via API
 */
export const updateFileAttribute = async (file, userId, sessionId) => {
  if (!file) {
    return;
  }

  const sid =
    sessionId ||
    getSavedSessionObj()?.session_id ||
    (await ensureSessionForUser(userId));

  const formData = new FormData();
  formData.append("file", file);
  formData.append("user_id", String(userId));
  formData.append("session_id", String(sid));

  try {
    const response = await apiClient.post(
      "/poirot/update_file_attribute/v1.0",
      formData
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

// Rename column
export const renameColumn = async (fileName, userSessionFileId, renameCol) => {
  return apiClient.post("/poirot/update_col_dtype/v1.0", {
    file_name: fileName,
    id: userSessionFileId,
    rename_col: renameCol,
  });
};

// Update dtype
export const updateColumnType = async (
  fileName,
  userSessionFileId,
  updateDtype
) => {
  return apiClient.post("/poirot/update_col_dtype/v1.0", {
    file_name: fileName,
    id: userSessionFileId,
    update_dtype: updateDtype,
  });
};

export const runQuery = async (fileName, userId, sessionId, question) => {
  try {
    const sid =
      sessionId ||
      getSavedSessionObj()?.session_id ||
      (await ensureSessionForUser(userId));

    const payload = new URLSearchParams();
    payload.append("file_name", fileName);
    payload.append("userid", String(userId));
    payload.append("sessionid", String(sid));
    payload.append("question", question);

    const response = await apiClient.post("/poirot/run_query", payload, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// services/api.js  (append below your existing exports)
export const createSession = async (userId) => {
  // GET /poirot/create_session?user_id=1
  const resp = await apiClient.get("/poirot/create_session", {
    params: { user_id: String(userId) },
  });
  return resp.data; // expected: { "user_id": "1", "session_id":"2" }
};

export const getSessionData = async (sessionId) => {
  // GET /poirot/get_session_data/{sessionId}
  const resp = await apiClient.get(`/poirot/get_session_data/${sessionId}`);
  return resp.data;
};

export const fetchUserHistory = async (userId) => {
  try {
    const resp = await apiClient.get("/poirot/view_user_session", {
      params: { user_id: userId },
    });

    // Map API response to match HistoryList expected format
    const historyList = (resp.data.Sucess || []).map((item) => ({
      id: item.Session_Id,
      name: item.Title, // this will be used in HistoryList as title
      messages: [], // optional, keep empty unless you want to fetch messages separately
      timestamp: Date.now(), // optional placeholder
    }));

    return historyList;
  } catch (error) {
    return [];
  }
};

export const userSignup = async (
  firstName,
  lastName,
  username,
  email,
  phone,
  password
) => {
  try {
    const payload = {
      username,
      email,
      phone: String(phone),
      password,
      ...(firstName && { first_name: firstName }),
      ...(lastName && { last_name: lastName }),
    };

    const response = await apiClient.post("/poirot/users/create/v1.0", payload);

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Signup failed. Please try again."
    );
  }
};

export const userLogin = async (identifier, password) => {
  try {
    // Assuming identifier is email for now; extend for username/phone if needed
    const payload = { email: identifier, password };

    const response = await apiClient.post("/poirot/users/login/v1.0", payload);

    // Assuming login returns similar user data; adjust based on actual API response
    const successData = response.data.Success?.[0] || response.data;
    if (!successData) {
      throw new Error("Login failed: Invalid credentials");
    }

    return {
      userId: successData.user_id,
      username: successData.username,
      email: successData.email,
      phone: successData.phone,
      firstName: successData.first_name,
      lastName: successData.last_name,
    };
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Invalid email or password"
    );
  }
};

// Fetch single user profile (view only, no edit)
export const fetchUserProfile = async (userId) => {
  if (!userId) return null;
  try {
    const resp = await apiClient.get("/poirot/users/view/v1.0", {
      params: { user_id: userId },
    });

    // Grab the first element from the Success array
    const userData = resp.data?.Success?.[0];
    if (!userData) return null;

    return {
      userId: userData.user_id,
      username: userData.username,
      firstName: userData.first_name,
      lastName: userData.last_name,
      email: userData.email,
      phone: userData.phone,
      password: userData.password,
    };
  } catch (error) {
    return null;
  }
};

export const getInsights = async (runQueryResponse) => {
  try {
    const response = await apiClient.post("/poirot/get_insights", {
      response_data: runQueryResponse, 
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching insights:", error);
    return null;
  }
};
